import { useEffect, useLayoutEffect, useRef } from "react";
import "./auslassung.css";

/**
 * Kompakt-Ebene (D-1, Inhaber 21.10.2026): Texte mit der Klasse „tk-ell“ stehen einzeilig mit Auslassung („…“) oder auf wenige Zeilen gekürzt (line-clamp).
 * Ist ein solcher Text tatsächlich abgeschnitten, bekommt er den vollen Text als Tooltip (title); passt er wieder, fällt der
 * Tooltip weg. Ein von Hand gesetzter title bleibt unberührt. Geprüft nach jedem Zeichnen und bei jeder Größenänderung.
 */
export function useAuslassungTitel<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const pruefe = () => {
    const wurzel = ref.current;
    if (!wurzel) return;
    for (const el of wurzel.querySelectorAll<HTMLElement>(".tk-ell")) {
      const zuLang = el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
      if (zuLang && (!el.title || el.dataset.autoTitel)) {
        el.title = (el.textContent ?? "").replace(/\s+/g, " ").trim();
        el.dataset.autoTitel = "1";
      } else if (!zuLang && el.dataset.autoTitel) {
        el.removeAttribute("title");
        delete el.dataset.autoTitel;
      }
    }
  };
  useLayoutEffect(pruefe);
  useEffect(() => {
    const wurzel = ref.current;
    if (!wurzel || typeof ResizeObserver === "undefined") return;
    const beobachter = new ResizeObserver(() => pruefe());
    beobachter.observe(wurzel);
    return () => beobachter.disconnect();
  }, []);
  return ref;
}

/**
 * Prüf-Chat 03.11.2026 (L13, L15, L17): gekürzter Text braucht seine volle Fassung. Für jede Stelle der App, die mit „…“ oder
 * line-clamp kürzt (auch ohne „tk-ell“), setzt dieser eine Zuhörer beim Überfahren mit der Maus den vollen Text als Tooltip,
 * wenn das Element tatsächlich abgeschnitten ist und keinen eigenen title hat. Am Handy bricht .tk-ell ohnehin um.
 */
export function gekuerztTitelUeberall(doc: Document = document): () => void {
  const pruefe = (start: EventTarget | null) => {
    let el = start instanceof Element ? (start as HTMLElement) : null;
    for (let i = 0; el && i < 4; i++, el = el.parentElement) {
      if (el.title || el.closest("[title]") !== null && el.closest("[title]") !== el) return;
      const cs = getComputedStyle(el);
      const kuerzt = cs.textOverflow === "ellipsis" || (cs.webkitLineClamp && cs.webkitLineClamp !== "none");
      if (!kuerzt) continue;
      const zuLang = el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
      const text = (el.textContent ?? "").replace(/\s+/g, " ").trim();
      if (zuLang && text) { el.title = text; el.dataset.autoTitel = "1"; }
      return;
    }
  };
  const zuhoerer = (e: Event) => pruefe(e.target);
  doc.addEventListener("pointerover", zuhoerer, { passive: true });
  doc.addEventListener("focusin", zuhoerer);
  return () => { doc.removeEventListener("pointerover", zuhoerer); doc.removeEventListener("focusin", zuhoerer); };
}
